import Spacer from '@berlitz/spacer'
import InstructorSearchResultCard from '@berlitzplatforms/micro.ui.instructor-search-result-card'
import InstructorSearchResult from '@classes/InstructorSearchResult'
import InstructorProfile from '@classes/InstructorProfile'
import Button from '@components/Button'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'
import { sortBy } from 'lodash'
import { useRouter } from 'next/router'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import { useBlockedInstructors, useFave } from './context'
import EmptyResults from './EmptyResults'
import {
  Footer,
  Grid,
  ResultsWrapper,
  SearchDetails,
  SearchHeader,
  SortSelect,
  SortWrapper,
  StyledH3,
  Tag,
} from './style'
import { FMIStudentProfileContext } from './context'
import BlockModal from './BlockModal'
import profile from '@pages/salesforce/instructors/profile'

interface SearchResultsProps {
  instructors?: InstructorSearchResultInfo[]
  loading?: boolean
  filters?: Record<string, unknown>
  pristine?: boolean
  setPristine: (_val: boolean) => void
  clearSort?: boolean
  readOnly?: boolean
  programId: string
  goToInstructor: (a: string, b?: string) => void
}

const SearchResults: React.FC<SearchResultsProps> = ({
  instructors = [],
  loading,
  filters = {},
  pristine,
  setPristine,
  clearSort,
  readOnly,
  goToInstructor,
}) => {
  const entryPerPage = 40
  const intl = useIntl()
  const fave = useFave()
  const blocked = useBlockedInstructors()
  const router = useRouter()
  const { isAdmin } = useContext(FMIStudentProfileContext)
  const [store, setStore] = useState<InstructorSearchResult>()
  const [results, setResults] = useState<InstructorSearchResultInfo[]>()
  const [sort, setSort] = useState('sortByScore')
  const [limit, setLimit] = useState(entryPerPage)
  const [entry, setEntry] = useState<IntersectionObserverEntry[]>()
  const [endObs, setEndObs] = useState<IntersectionObserver>()
  const [modalOpen, setModalOpen] = useState(false)

  const sortOptions = [
    {
      label: intl.formatMessage({
        id: 'Most Available (Week)',
        defaultMessage: 'Most Available (Week)',
      }),
      value: 'sortByScore',
    },
    {
      label: intl.formatMessage({
        id: 'Alphabetical (A-Z)',
        defaultMessage: 'Alphabetical (A-Z)',
      }),
      value: 'sortByName',
    },
  ]

  const onSortChange = (value: string) => setSort(value)

  useEffect(() => {
    if (store) {
      store.SortValue = sort
      setResults(store.getResults(filters).slice(0, limit))
    }
  }, [sort])

  useEffect(() => {
    if (store) {
      setLimit(entryPerPage) // reset limit
      setResults(store.getResults(filters).slice(0, entryPerPage))
    }
  }, [filters])

  useEffect(() => {
    const data = new InstructorSearchResult(instructors)
    const newVal = JSON.stringify(instructors)
    const oldVal = JSON.stringify(store?.instructors || [])

    if (oldVal === newVal) {
      return
    }

    setStore(data)
    setResults(data.getResults({}).slice(0, limit))
  }, [instructors])

  useEffect(() => {
    if (clearSort) {
      setSort('sortByScore')
    }
  }, [clearSort])

  // need to do this so react state changes would take effect
  const endListener = (entries: IntersectionObserverEntry[]) => setEntry(entries)

  useEffect(() => {
    if (entry?.[0]?.isIntersecting) {
      setLimit(limit + entryPerPage)
    }
  }, [entry])

  useEffect(() => {
    if (store) {
      setResults(store.getResults(filters).slice(0, limit))
    }
  }, [limit])

  const onRefChange = useCallback((node) => {
    if (node) {
      const options = {
        rootMargin: '0px',
        threshold: 0.1,
      }
      const obs = new IntersectionObserver(endListener, options)
      setEndObs(obs)
      obs?.observe(node)
    } else {
      // element has unmounted so we dont need to observe
      endObs?.disconnect()
    }
  }, [])

  return (
    <ResultsWrapper>
      {!results?.length && !loading ? (
        <EmptyResults />
      ) : (
        <>
          <SearchHeader>
            <SearchDetails>
              <StyledH3>
                {loading ? (
                  <Skeleton width="125px" height="18px" />
                ) : (
                  <FormattedMessage id={'Search results'} defaultMessage={'Search results'} />
                )}
              </StyledH3>

              {loading ? (
                <Skeleton width="110px" height="18px" />
              ) : (
                <Tag>
                  <FormattedMessage
                    id={'{count, plural, =0 {0 instructors} one {1 instructor} other {# instructors}}'}
                    defaultMessage={'{count, plural, =0 {0 instructors} one {1 instructor} other {# instructors}}'}
                    values={{ count: store?.getResults(filters)?.length }}
                  />
                </Tag>
              )}
            </SearchDetails>

            <SortWrapper>
              {loading ? (
                <>
                  <Skeleton width="28px" height="18px" />
                  <Skeleton width="200px" height="34px" />
                </>
              ) : (
                <>
                  <span>
                    <FormattedMessage id={'Sort'} defaultMessage={'Sort'} />
                  </span>
                  <SortSelect defaultValue={[sort || 'sortByScore']} options={sortOptions} onChange={onSortChange} />
                </>
              )}
            </SortWrapper>
          </SearchHeader>
          <Spacer size="xxxs" />
          <Spacer size="xxs" />
          <Spacer size="xs" />
          <Grid>
            {loading ? (
              <>
                <InstructorSearchResultCard loading />
                <InstructorSearchResultCard loading />
                <InstructorSearchResultCard loading />
                <InstructorSearchResultCard loading />
              </>
            ) : (
              <>
                {(pristine ? results?.slice(0, 4) : results)?.map((result) => {
                  const isFaved = fave.isFaved(result.InstructorId)
                  const bookText = router.query.sessionId ? 'Re-book' : 'Book'
                  const Profile = new InstructorProfile(result)
                  return (
                    <InstructorSearchResultCard
                      key={result.InstructorId}
                      image={Profile.Avatar}
                      title={result.Name}
                      isReadOnly={readOnly}
                      onTitleClick={() => goToInstructor('profile', result.InstructorId)}
                      onAvatarClick={() => goToInstructor('profile', result.InstructorId)}
                      onBook={() => goToInstructor('availability', result.InstructorId)}
                      description={
                        result.DisplayLang
                          ? intl.formatMessage(
                              {
                                id: 'Speaks {lang}',
                                defaultMessage: 'Speaks {lang}',
                              },
                              { lang: result.DisplayLang }
                            )
                          : ''
                      }
                      withFaveBtn={!isAdmin}
                      isFaved={isFaved}
                      onFaveClick={() => {
                        if (fave.enableActions) {
                          const newFaves = fave.faveInstructors.filter(
                            ({ InstructorId }) => InstructorId !== result.InstructorId
                          )

                          if (isFaved) {
                            fave.setFaveInstructors(newFaves)
                            fave.deleteRelatedInstructors({
                              variables: fave.getPayload('delete', [result]),
                            })
                          } else {
                            fave.setFaveInstructors(sortBy([...newFaves, result], ['Name']))
                            fave.putRelatedInstructors({
                              variables: fave.getPayload('put', [result]),
                            })
                          }
                        }
                      }}
                      bookText={intl.formatMessage({
                        id: bookText,
                        defaultMessage: bookText,
                      })}
                      isBlocked={blocked.isBlocked(result.InstructorId)}
                      withBlockedBtn={blocked.allowed}
                      onBlockedClick={() => {
                        if (blocked.enableActions) {
                          blocked.setSelectedInstructor(result)
                          setModalOpen(true)
                        }
                      }}
                      disableFaveActions={!fave.enableActions}
                      disableBlockActions={!blocked.enableActions}
                    />
                  )
                })}
                {
                  // add filler so width for the single result wont go 100%
                  results?.length === 1 && <span> </span>
                }
                {store && store.getResults(filters).length > limit && !pristine && (
                  <>
                    <div ref={onRefChange} id="last-entry">
                      <InstructorSearchResultCard loading />
                    </div>
                    <InstructorSearchResultCard loading />
                    <InstructorSearchResultCard loading />
                    <InstructorSearchResultCard loading />
                  </>
                )}
              </>
            )}
          </Grid>
          <Spacer size="lg" />

          <Footer>
            {pristine && !loading && (store?.getResults(filters) || [])?.length > 4 ? (
              <Button onClick={() => setPristine(false)}>
                <FormattedMessage id="Show more" defaultMessage="Show more" />
              </Button>
            ) : (
              loading && <Skeleton width="125px" height="36px" />
            )}
          </Footer>

          <Spacer size="lg" />
        </>
      )}

      <BlockModal
        open={modalOpen && !!blocked.selectedInstructor}
        onClose={() => {
          blocked.setSelectedInstructor(undefined)
          setModalOpen(false)
        }}
        onCompleted={(id) => {
          setResults(results?.filter(({ InstructorId }) => InstructorId !== id) || [])
          setStore(new InstructorSearchResult(results?.filter(({ InstructorId }) => InstructorId !== id) || []))
        }}
      />
    </ResultsWrapper>
  )
}

export default SearchResults
